/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Cashback reserve component
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.12.0
 * @since 1.12.0
 */

import { useCallback } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Button } from '../../../../../components/common';
import ArrowTooltip from '../../../../../components/common/Tooltip/Tooltip';
import GTMOperations from '../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { IGTMPushAnalyticPayload } from '../../../../../modules/analytics/GoogleTagManager/types/GTMActionTypes';
import { ICommonTranslation } from '../../../../../modules/internationalization/types';
import { IPageCashbackControlTranslation } from '../../../../../modules/internationalization/types/pages/cashback';
import { modalOpen } from '../../../../../modules/modal/modalActions';
import { IModalOpenPayload } from '../../../../../modules/modal/types/modalActionTypes';
import { IModalRenderProps } from '../../../../../modules/modal/types/modalTypes';
import { IRootReducer } from '../../../../../reducers/rootReducer';
import { stringFormatParams } from '../../../../../utils/stringFormat';
import { IPageCashbackReducer } from '../../../pageCashbackReducer';
import {
    makeSelectCashbackReserveAvailable,
    makeSelectCashbackReserveTooltip,
} from '../../../selectors/cashbackControlSelectors';
import { makeSelectCashbackMeta } from '../../../selectors/cashbackSelectors';
import { ICashbackControlConnectedService } from '../../../types/cashbackControlTypes';
import CashbackReserveModal from './CashbackReserveModal';

interface ICashbackReserve {
    currentService: ICashbackControlConnectedService | null;
    pushGtmAnalytic: (payload: IGTMPushAnalyticPayload) => void;
    tabControlTranslation: IPageCashbackControlTranslation;
    translationCommon: ICommonTranslation;
}

const CashbackReserve = (props: ICashbackReserve & IConnectedProps & IDispatchToProps) => {

    const {
        currentService,
        isReserveAvailable,
        meta,
        reserveTooltip,
        tabControlTranslation,
        translationCommon,

        openModal,
        pushGtmAnalytic,
    } = props;
    /*
    * -------------------------------------------
    *               Handlers
    * -------------------------------------------
    */

    const onCloseCallback = useCallback((isBackgroundClose: boolean) => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.clickOnClosePopUpCashbackPage(
                stringFormatParams(tabControlTranslation.reserveModal.start.title, { name: currentService?.title }),
                isBackgroundClose,
            ),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [currentService, pushGtmAnalytic, tabControlTranslation.reserveModal.start.title]);

    const onButtonClick = useCallback((isReserveButtonDisabled: boolean) => {
        pushGtmAnalytic({
            operation: GTMOperations.cashback.clickOnButtonReserve(isReserveButtonDisabled),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    }, [pushGtmAnalytic]);

    const onReserveHandler = useCallback(() => {
        if (!currentService) return;

        openModal({
            onCloseCallback: () => onCloseCallback(false),
            onBackgroundCloseCallback: () => onCloseCallback(true),
            render: (props: IModalRenderProps) => <CashbackReserveModal
                maxScore={meta.balance}
                service={currentService}
                translation={tabControlTranslation.reserveModal}
                translationCommon={translationCommon}
                onCloseModal={props.onCloseModal}
            />,
        });
    }, [
        currentService,
        meta.balance,
        onCloseCallback,
        openModal,
        tabControlTranslation,
        translationCommon,
    ]);

    /*
    * -------------------------------------------
    *               Render
    * -------------------------------------------
    */

    const getReserveTooltipMsg = useCallback(() => {
        return isReserveAvailable
            ? currentService ? '' : reserveTooltip
            : reserveTooltip;
    }, [currentService, isReserveAvailable, reserveTooltip]);

    const isReserveButtonDisabled = !currentService || !isReserveAvailable;

    return (
        <ArrowTooltip title={getReserveTooltipMsg()} titleAlign="center">
            <span onClick={() => onButtonClick(isReserveButtonDisabled)}>
                <Button disabled={isReserveButtonDisabled} kind="medium" onClick={onReserveHandler}>
                    {tabControlTranslation.btnReserve}
                </Button>
            </span>
        </ArrowTooltip>
    );
};

export interface IConnectedProps extends Pick<IPageCashbackReducer, 'meta'> {
    isReserveAvailable: boolean;
    reserveTooltip: string;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isReserveAvailable: makeSelectCashbackReserveAvailable(),
    meta: makeSelectCashbackMeta(),
    reserveTooltip: makeSelectCashbackReserveTooltip(),
});

export interface IDispatchToProps {
    openModal: (payload: IModalOpenPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchToProps => ({
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CashbackReserve);
